import { ChangeEvent, JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
	createResource,
	getResourceContent,
	getResourceInfo,
	updateResourceContent
} from "resource/ResourceService";

import {
	InformationRight,
	ReportFieldRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";

import { ImageField as UploadField } from "../FieldService";

export type UploadInputProps = {
	field: UploadField;
	rights: ReportFieldRights;
	editable: boolean;

	onChange: (uuid: string, value: string) => void;
};

const MAX_SIZE_MB = 10;

type FileNames = {
	[key: number]: string;
};

export default function UploadInput(
	props: Readonly<UploadInputProps>
): JSX.Element {
	const { t } = useTranslation();

	const [files, setFiles] = useState<FileNames>({});
	const [error, setError] = useState<string>();

	// We only load the files information on the initial render.
	useEffect(() => {
		Promise.all(
			props.field.value.map(id =>
				getResourceInfo(id).then(resource => ({ [id]: resource.name }))
			)
		).then((entries: FileNames[]) => {
			setFiles(entries.reduce((a, b) => ({ ...a, ...b }), {}));
		});
	}, []);

	async function uploadFiles(file: File): Promise<void> {
		setError(undefined);
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			setError(t("fileMaxSize", { size: `${MAX_SIZE_MB}MiB` }));
			return;
		}

		const data = new FormData();
		data.append("file", file);
		createResource({
			name: file.name,
			mediaType: file.type,
			userRights: []
		})
			.then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			})
			.then(id =>
				updateResourceContent(id, data).then(() => {
					setFiles(previous => ({ ...previous, [id]: file.name }));
					const newValue = props.field.value.concat(id);
					props.onChange(props.field.uuid, JSON.stringify(newValue));
				})
			)
			.catch(() => {
				throw new Error("failed to create resource");
			});
	}

	const uploadFile = useCallback(
		(file: File): void => {
			uploadFiles(file);
		},
		[props.onChange, props.field.uuid, props.field.value, files]
	);

	function onSelectFile(event: ChangeEvent<HTMLInputElement>): void {
		if (!event.target.files || event.target.files.length === 0) {
			return;
		}

		const file = event.target.files[0];
		uploadFile(file);
	}

	const openFile = useCallback((id: number, name: string): void => {
		getResourceContent(id).then(data => {
			const url = URL.createObjectURL(data);
			const w = window.open(url, name);
			w?.addEventListener("beforeunload", () => URL.revokeObjectURL(url));
		});
	}, []);

	const deleteFile = useCallback(
		(id: number): void => {
			const newValue = props.field.value.filter(i => i !== id);
			props.onChange(props.field.uuid, JSON.stringify(newValue));
		},
		[props.onChange, props.field.uuid, props.field.value]
	);

	function toId(uuid: string): string {
		const separatorIndex = uuid.indexOf("#");
		return separatorIndex === -1 ? uuid : uuid.substring(0, separatorIndex);
	}

	return (
		<Grid container>
			<Grid item container columnSpacing={2} alignItems="center">
				<Grid item>
					<Typography>{props.field.name}</Typography>
				</Grid>
				<Grid item>
					<TextField
						type="file"
						variant="standard"
						disabled={
							!props.editable ||
							props.rights[toId(props.field.uuid)][
								InformationRight.WRITE
							] === false
						}
						error={error !== undefined}
						helperText={error ?? ""}
						onChange={onSelectFile}
					/>
				</Grid>
			</Grid>

			<Grid item container direction="column">
				{props.field.value.map((id, index) => (
					<Grid
						item
						container
						key={id}
						spacing={1}
						alignItems="center">
						<Grid item>
							<Link
								key={index}
								sx={{ cursor: "pointer" }}
								variant="subtitle1"
								onClick={() =>
									openFile(id, files[index] ?? id)
								}>
								{files[id] ?? id}
							</Link>
						</Grid>
						{props.editable && (
							<Grid item>
								<IconButton onClick={() => deleteFile(id)}>
									<DeleteOutlineIcon />
								</IconButton>
							</Grid>
						)}
					</Grid>
				))}
			</Grid>
		</Grid>
	);
}
